import { ZodError, ZodType } from 'zod';
import { FieldError, FieldErrors, FieldValues } from 'react-hook-form';
import { SentryMonitoringService } from '@libs/frontend-services';

/**
 * `react-hook-form` and `zod` have a version compability issued outlined here:
 * https://github.com/react-hook-form/react-hook-form/issues/12816
 *
 * This utility is a custom resolver that converts a Zod schema to a Hook Form-compatible schema.
 * It also converts ZodError to Hook Form-compatible FieldErrors.
 */

// Utility to convert ZodError to Hook Form-compatible FieldErrors
const zodToHookFormErrors = (zodError: ZodError): FieldErrors => {
  const errors: FieldErrors = {};

  for (const issue of zodError.issues) {
    const path = issue.path.join('.') || 'root';
    errors[path] = {
      type: issue.code,
      message: issue.message,
    } as FieldError;
  }

  return errors;
};

// Custom resolver for useForm()
export const customZodResolver = (schema: ZodType) => {
  return async (
    values: FieldValues,
  ): Promise<{
    values: FieldValues;
    errors: FieldErrors;
  }> => {
    try {
      const result = await schema.safeParseAsync(values);

      if (result.success) {
        return {
          values: result.data as FieldValues,
          errors: {},
        };
      } else {
        return {
          values: {},
          errors: zodToHookFormErrors(result.error),
        };
      }
    } catch (error) {
      const sentryMonitoringService = new SentryMonitoringService();
      if (error instanceof Error) {
        sentryMonitoringService.captureException(error);
      } else {
        sentryMonitoringService.captureException(
          new Error('An unknown error occurred during validation'),
        );
      }
      return {
        values: {},
        errors: {
          root: {
            type: 'unknown',
            message: 'An unknown error occurred during validation',
          } as FieldError,
        },
      };
    }
  };
};
